/**
 * Category Form Modal Component
 *
 * Modal for creating and editing categories.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { z } from 'zod';
import { X } from 'lucide-react';
import { Button, Input, Select, ColorPicker } from '@/components/ui';
import { useCreateCategory, useUpdateCategory, useFlatCategories } from '../../raw-products/hooks/useCategories';
import type { CategoryTreeNode } from '@bstock/shared';

const categorySchema = z.object({
  name: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres').max(100),
  code: z.string().min(2).max(50).optional().or(z.literal('')),
  parentId: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface CategoryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  category?: CategoryTreeNode | null;
  parentId?: string | null;
}

export function CategoryFormModal({
  isOpen,
  onClose,
  category,
  parentId,
}: CategoryFormModalProps) {
  const isEditing = !!category;
  const { data: categories, isLoading: categoriesLoading } = useFlatCategories();

  // Build tree options for category select
  const categoryTreeOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.name,
    depth: cat.depth,
  })) ?? [];

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      code: '',
      parentId: null,
      color: null,
    },
  });

  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (category) {
        form.reset({
          name: category.name,
          code: category.code,
          parentId: category.parentId,
          color: category.color,
        });
      } else {
        form.reset({
          name: '',
          code: '',
          parentId: parentId ?? null,
          color: null,
        });
      }
    }
  }, [isOpen, category, parentId, form]);

  const handleSubmit = async (data: CategoryFormData) => {
    try {
      if (isEditing && category) {
        await updateMutation.mutateAsync({
          id: category.id,
          data: {
            name: data.name,
            color: data.color,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: data.name,
          code: data.code || undefined,
          parentId: data.parentId || undefined,
          color: data.color,
        });
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-dark-900/40 z-60"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-dark-500">
                {isEditing ? 'Modifier la categorie' : 'Nouvelle categorie'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="p-6 space-y-4">
            {/* Parent - only for creation mode */}
            {!isEditing && (
              <Select
                label="Categorie parente"
                value={form.watch('parentId') ?? ''}
                onChange={(value) => form.setValue('parentId', value || null)}
                treeOptions={categoryTreeOptions}
                placeholder="Aucune (categorie racine)"
                disabled={categoriesLoading}
              />
            )}

            {/* Name */}
            <Input
              label="Nom *"
              {...form.register('name')}
              error={form.formState.errors.name?.message}
              placeholder="Ex: Viandes & Volailles"
            />

            {/* Code */}
            {!isEditing && (
              <Input
                label="Code"
                {...form.register('code')}
                error={form.formState.errors.code?.message}
                placeholder="Auto-genere si vide"
                helperText="Code unique pour identifier la categorie"
              />
            )}

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                Couleur
              </label>
              <ColorPicker
                value={form.watch('color')}
                onChange={(color) => form.setValue('color', color)}
              />
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={onClose}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={form.handleSubmit(handleSubmit)}
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {isEditing ? 'Enregistrer' : 'Creer'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
